/**
 * Reason codes and their Russian operator text.
 *
 * This is a literal copy of `packages/domain/src/reason-codes.ts`
 * (normative source: STRATEGY_SPEC.md section 7). `apps/web` is not allowed
 * to depend on `@btcbot/domain` as a package (it is not declared in the
 * frozen `apps/web/package.json`), so the text is duplicated here on
 * purpose rather than re-derived. Do NOT invent new wording here — if the
 * domain package's text changes, copy the change over verbatim.
 */
export const ReasonCode = {
  // --- Directional decisions, normal mode -----------------------------------
  RULE_OVERBOUGHT: 'RULE_OVERBOUGHT',
  RULE_RSI_ABOVE_NEUTRAL: 'RULE_RSI_ABOVE_NEUTRAL',
  RULE_SMOOTHED_ABOVE_NEUTRAL: 'RULE_SMOOTHED_ABOVE_NEUTRAL',
  RULE_BOTH_BELOW_NEUTRAL: 'RULE_BOTH_BELOW_NEUTRAL',

  // --- Directional decisions, post-impulse mode -----------------------------
  POST_IMPULSE_DAILY_LONG: 'POST_IMPULSE_DAILY_LONG',
  POST_IMPULSE_DAILY_SHORT: 'POST_IMPULSE_DAILY_SHORT',

  // --- No trade because of the data or the neutral band ---------------------
  NO_TRADE_NEUTRAL_ZONE: 'NO_TRADE_NEUTRAL_ZONE',
  NO_TRADE_INSUFFICIENT_DATA: 'NO_TRADE_INSUFFICIENT_DATA',
  NO_TRADE_DATA_GAP: 'NO_TRADE_DATA_GAP',
  NO_TRADE_STALE_DATA: 'NO_TRADE_STALE_DATA',

  // --- Skips by policy and calendar -----------------------------------------
  SKIP_STRATEGY_STOPPED: 'SKIP_STRATEGY_STOPPED',
  SKIP_ENTRIES_PAUSED: 'SKIP_ENTRIES_PAUSED',
  SKIP_NON_TRADING_DAY: 'SKIP_NON_TRADING_DAY',
  SKIP_MISSED_WINDOW: 'SKIP_MISSED_WINDOW',
  SKIP_DAILY_LIMIT: 'SKIP_DAILY_LIMIT',
  SKIP_POSITION_OPEN: 'SKIP_POSITION_OPEN',
  SKIP_UNKNOWN_PENDING_ORDER: 'SKIP_UNKNOWN_PENDING_ORDER',
  SKIP_TP_SAME_DATE: 'SKIP_TP_SAME_DATE',
  SKIP_MANUAL_CLOSE_SAME_DATE: 'SKIP_MANUAL_CLOSE_SAME_DATE',
  SKIP_IMPULSE_COOLDOWN: 'SKIP_IMPULSE_COOLDOWN',

  // --- Skips by risk and venue state ----------------------------------------
  SKIP_RISK_LIMIT: 'SKIP_RISK_LIMIT',
  SKIP_RISK_MANUAL_APPROVAL_REQUIRED: 'SKIP_RISK_MANUAL_APPROVAL_REQUIRED',
  SKIP_LIQUIDATION_REVIEW_REQUIRED: 'SKIP_LIQUIDATION_REVIEW_REQUIRED',
  SKIP_LIQUIDATION_BUFFER: 'SKIP_LIQUIDATION_BUFFER',
  SKIP_FUNDING_RATE: 'SKIP_FUNDING_RATE',
  SKIP_SLIPPAGE_LIMIT: 'SKIP_SLIPPAGE_LIMIT',
  SKIP_LEVERAGE_UNAVAILABLE: 'SKIP_LEVERAGE_UNAVAILABLE',
  SKIP_VENUE_CONFIG_CHANGED: 'SKIP_VENUE_CONFIG_CHANGED',
  SKIP_CLOCK_SKEW: 'SKIP_CLOCK_SKEW',
  SKIP_STATE_NOT_RECONCILED: 'SKIP_STATE_NOT_RECONCILED',
} as const;

export type ReasonCode = (typeof ReasonCode)[keyof typeof ReasonCode];

export const ALL_REASON_CODES: readonly ReasonCode[] = Object.freeze(
  Object.values(ReasonCode),
) as readonly ReasonCode[];

/**
 * Russian operator-facing text for every reason code.
 * The UI must render this, never the bare identifier.
 */
export const REASON_TEXT_RU: Readonly<Record<ReasonCode, string>> = Object.freeze({
  RULE_OVERBOUGHT: 'RSI выше 70: зона перекупленности, вход в SHORT.',
  RULE_RSI_ABOVE_NEUTRAL: 'RSI не ниже 50: восходящий режим, вход в LONG.',
  RULE_SMOOTHED_ABOVE_NEUTRAL:
    'RSI ниже 50, но сглаженная линия не ниже 50: сглаженная линия указывает восходящий режим, вход в LONG.',
  RULE_BOTH_BELOW_NEUTRAL: 'Обе линии ниже 50: нисходящий режим, вход в SHORT.',

  POST_IMPULSE_DAILY_LONG:
    'Первый вход после импульсной паузы: дневная сглаженная линия RSI не ниже 50, вход в LONG.',
  POST_IMPULSE_DAILY_SHORT:
    'Первый вход после импульсной паузы: дневная сглаженная линия RSI ниже 49, вход в SHORT.',

  NO_TRADE_NEUTRAL_ZONE:
    'Дневная сглаженная линия RSI в нейтральной зоне [49, 50): вход не выполняется.',
  NO_TRADE_INSUFFICIENT_DATA:
    'Недостаточно закрытых свечей для расчета RSI и сглаженной линии: вход запрещен.',
  NO_TRADE_DATA_GAP: 'В серии свечей обнаружен пропуск: данные недостоверны, вход запрещен.',
  NO_TRADE_STALE_DATA:
    'Рыночные данные устарели или mark/index/last расходятся сверх допустимого порога: вход запрещен.',

  SKIP_STRATEGY_STOPPED: 'Стратегия остановлена оператором.',
  SKIP_ENTRIES_PAUSED: 'Новые входы приостановлены. Мониторинг и защита позиции продолжаются.',
  SKIP_NON_TRADING_DAY: 'Выходной день: новые входы запрещены.',
  SKIP_MISSED_WINDOW: 'Окно входа пропущено. Пропущенная сделка не догоняется.',
  SKIP_DAILY_LIMIT: 'В эту локальную дату вход уже создавался: повторный вход запрещен.',
  SKIP_POSITION_OPEN: 'Уже есть открытая позиция: новый вход запрещен.',
  SKIP_UNKNOWN_PENDING_ORDER:
    'На площадке обнаружен неизвестный ордер в работе: новый вход запрещен до выяснения.',
  SKIP_TP_SAME_DATE:
    'В эту локальную дату позиция закрылась по Take Profit: новый вход в этот день запрещен.',
  SKIP_MANUAL_CLOSE_SAME_DATE:
    'В эту локальную дату позиция закрыта вручную: новые входы заблокированы до следующей сессии.',
  SKIP_IMPULSE_COOLDOWN: 'Действует пауза после импульсного роста: вход пропускается.',

  SKIP_RISK_LIMIT: 'Нарушен лимит риска: вход отклонен риск-движком.',
  SKIP_RISK_MANUAL_APPROVAL_REQUIRED:
    'Достигнут лимит убытков: новые входы приостановлены до ручного подтверждения администратора.',
  SKIP_LIQUIDATION_REVIEW_REQUIRED:
    'Была ликвидация: входы заблокированы до ручного разбора и подтверждения администратора.',
  SKIP_LIQUIDATION_BUFFER:
    'Недостаточный запас между расчетным Stop Loss и ценой ликвидации: вход отклонен.',
  SKIP_FUNDING_RATE:
    'Ставка funding превышает допустимый порог для платящей стороны: вход пропущен.',
  SKIP_SLIPPAGE_LIMIT:
    'Ожидаемое проскальзывание или price impact превышает допустимый предел: вход отменен.',
  SKIP_LEVERAGE_UNAVAILABLE:
    'Площадка не разрешает требуемое плечо 30x: вход пропущен, плечо не подменяется.',
  SKIP_VENUE_CONFIG_CHANGED:
    'Конфигурация контракта или счета изменилась относительно принятого snapshot: входы приостановлены до повторной проверки.',
  SKIP_CLOCK_SKEW: 'Расхождение часов с сервером площадки превышает допуск: вход запрещен.',
  SKIP_STATE_NOT_RECONCILED:
    'Локальное состояние не сверено с площадкой: вход запрещен до успешной сверки.',
});

export function reasonTextRu(code: string): string {
  if (code in REASON_TEXT_RU) return REASON_TEXT_RU[code as ReasonCode];
  // Defensive fallback: the API contract should never emit an unknown code,
  // but a stale frontend build talking to a newer API must not crash or show
  // a raw identifier as if it were prose.
  return `Неизвестная причина решения: ${code}`;
}
